import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

import useAuthGuard from '../../../hooks/useAuthGuard';
import { favoritesService } from '../../../services';
import { getCollaborations } from '../../../services/collaborationService';
import { deletePoster, getActivePosters } from '../../../services/posterService';
import { getCurrentUser } from '../../../utils/currentUser';
import imageStorage from '../../../utils/indexedDB.js';
import { LoginModal } from '../../ui/LoginModal';
import PosterLightbox from '../../ui/PosterLightbox';

import CollaborationImage from './CollaborationImage';

const CollaborationGrid = ({ onCollaborationClick, onBookmarkToggle }) => {
  // 2026-04-17 登录守卫：未登录点击收藏弹 LoginModal
  const {
    requireLogin,
    isLoginModalOpen,
    handleLoginSuccess,
    handleClose: handleLoginModalClose,
  } = useAuthGuard();
  const [collaborations, setCollaborations] = useState([]);
  const [posters, setPosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookmarkStates, setBookmarkStates] = useState({});
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [posterToDelete, setPosterToDelete] = useState(null); // { id, storagePath }
  const loadedRef = useRef(false); // 防止重复加载

  // 获取协作数据 + posters
  useEffect(() => {
    // 防止 React Strict Mode 重复执行
    if (loadedRef.current) {
      console.log('[CollaborationGrid] Already loaded, skipping...');
      return;
    }

    const fetchCollaborations = async () => {
      console.time('[CollaborationGrid] 🕐 Total Load Time');
      loadedRef.current = true; // 标记为已加载
      try {
        setLoading(true);

        // 并行拉取 collaborations 和 posters
        const [collabResult, posterResult] = await Promise.all([
          getCollaborations(),
          getActivePosters(),
        ]);

        // 获取当前登录用户（用于判断是否显示 poster 删除按钮）
        const currentUser = await getCurrentUser();
        setCurrentUserId(currentUser?.id || null);

        if (collabResult.success) {
          setCollaborations(collabResult.data);
          await checkBookmarkStatuses(collabResult.data);
        } else {
          setError(collabResult.error);
        }

        if (posterResult.success) {
          setPosters(posterResult.data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        console.timeEnd('[CollaborationGrid] 🕐 Total Load Time');
        setLoading(false);
      }
    };

    // 检查收藏状态的函数（并行查询优化）
    const checkBookmarkStatuses = async collaborationsData => {
      try {
        const currentUser = await getCurrentUser();
        console.log('[CollaborationGrid] Current user:', currentUser);

        if (!currentUser?.id) {
          console.warn(
            '[CollaborationGrid] User not logged in, skipping bookmark status check'
          );
          return;
        }

        // 🔧 【性能优化】并行查询所有收藏状态，而不是串行查询
        console.log(
          `[CollaborationGrid] 🔄 批量检查 ${collaborationsData.length} 个协作的收藏状态...`
        );
        const bookmarkPromises = collaborationsData.map(collaboration =>
          favoritesService
            .checkFavoriteStatus('collaboration', collaboration.id)
            .then(result => ({
              id: collaboration.id,
              isFavorited: result.success ? result.data.isFavorited : false,
            }))
            .catch(error => {
              console.error(
                `[CollaborationGrid] Error checking bookmark for ${collaboration.id}:`,
                error
              );
              return { id: collaboration.id, isFavorited: false };
            })
        );

        const results = await Promise.all(bookmarkPromises);
        const bookmarkStatuses = {};
        results.forEach(result => {
          bookmarkStatuses[result.id] = result.isFavorited;
        });

        setBookmarkStates(bookmarkStatuses);
        console.log(
          '[CollaborationGrid] ✅ 批量检查完成，收藏状态:',
          bookmarkStatuses
        );
      } catch (error) {
        console.error(
          '[CollaborationGrid] Error checking bookmark statuses:',
          error
        );
      }
    };

    fetchCollaborations();

    // 监听新协作发布事件
    const handleNewCollaboration = () => {
      fetchCollaborations();
    };

    window.addEventListener('collaboration:created', handleNewCollaboration);

    return () => {
      window.removeEventListener(
        'collaboration:created',
        handleNewCollaboration
      );
    };
  }, []);

  // 处理 poster 删除（确认后调用）
  const handleDeletePoster = async ({ id, storagePath }) => {
    setPosterToDelete(null);
    const result = await deletePoster(id, storagePath);
    if (result.success) {
      setPosters(prev => prev.filter(p => p.id !== id));
    } else {
      console.error('[CollaborationGrid] Failed to delete poster:', result.error);
    }
  };

  // 处理收藏按钮点击
  const handleBookmarkClick = async (e, collaborationId) => {
    e.stopPropagation();

    // 2026-04-17 登录守卫：未登录直接弹 LoginModal 并中止
    if (!requireLogin()) return;

    try {
      const currentUser = await getCurrentUser();
      if (!currentUser?.id) {
        console.error(
          '[CollaborationGrid] User not logged in, cannot bookmark'
        );
        return;
      }

      const currentState = bookmarkStates[collaborationId] || false;
      console.log(
        `[CollaborationGrid] Toggling bookmark for collaboration ${collaborationId}, current state:`,
        currentState
      );

      // 调用收藏服务切换状态
      const result = await favoritesService.toggleFavorite(
        'collaboration',
        collaborationId,
        !currentState
      );

      if (result.success) {
        // 更新本地状态
        const newState = !currentState;
        setBookmarkStates(prev => ({
          ...prev,
          [collaborationId]: newState,
        }));

        // 调用父组件的回调（如果存在）
        if (onBookmarkToggle) {
          onBookmarkToggle(collaborationId);
        }

        console.log(
          `[CollaborationGrid] Collaboration ${collaborationId} ${
            newState ? 'bookmarked' : 'unbookmarked'
          } successfully`
        );

        // 验证收藏状态是否正确保存
        setTimeout(async () => {
          try {
            const verifyResult = await favoritesService.checkFavoriteStatus(
              'collaboration',
              collaborationId
            );
            console.log(
              `[CollaborationGrid] Verified bookmark status for collaboration ${collaborationId}:`,
              verifyResult.data.isFavorited
            );
          } catch (error) {
            console.error(
              `[CollaborationGrid] Failed to verify bookmark status:`,
              error
            );
          }
        }, 100);
      } else {
        console.error(
          '[CollaborationGrid] Bookmark operation failed:',
          result.error
        );
      }
    } catch (error) {
      console.error('[CollaborationGrid] Error in bookmark operation:', error);
    }
  };

  if (loading) {
    return (
      <div className='flex-1 flex items-center justify-center py-12'>
        <div className='text-gray-500'>Loading collaborations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='flex-1 flex items-center justify-center py-12'>
        <div className='text-red-500'>
          Error loading collaborations: {error}
        </div>
      </div>
    );
  }

  if (collaborations.length === 0 && posters.length === 0) {
    return (
      <div className='flex-1 flex items-center justify-center py-12'>
        <div className='text-gray-500'>No collaborations found.</div>
      </div>
    );
  }

  return (
    <div className='flex-1'>
      {/* Poster Lightbox */}
      {lightboxUrl && (
        <PosterLightbox
          imageUrl={lightboxUrl}
          onClose={() => setLightboxUrl(null)}
        />
      )}

      {/* Delete confirmation modal */}
      {posterToDelete && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 px-6'>
          <div className='bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl'>
            <p className='text-gray-900 font-semibold text-lg mb-2'>Delete this poster?</p>
            <p className='text-gray-500 text-sm mb-6'>This cannot be undone.</p>
            <div className='flex gap-3'>
              <button
                onClick={() => setPosterToDelete(null)}
                className='flex-1 border border-gray-300 text-gray-700 py-3 rounded-xl font-medium text-sm hover:bg-gray-50 transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeletePoster(posterToDelete)}
                className='flex-1 bg-red-500 text-white py-3 rounded-xl font-medium text-sm hover:bg-red-600 transition-colors'
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4'>

        {/* ── Poster cards (anonymous uploads) ── */}
        {posters.map(poster => (
          <div
            key={`poster-${poster.id}`}
            className='group rounded-lg overflow-hidden hover:scale-105 transition-all duration-300 cursor-pointer'
            onClick={() => setLightboxUrl(poster.image_url)}
          >
            <div className='relative'>
              <img
                src={poster.image_url}
                alt='Poster'
                className='w-full aspect-square object-cover'
                onError={e => {
                  e.target.style.display = 'none';
                }}
              />
              {/* Poster badge */}
              <span className='absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-0.5 rounded-full'>
                Poster
              </span>
              {/* Delete button — only visible to the poster owner on hover */}
              {currentUserId && poster.user_id === currentUserId && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    setPosterToDelete({ id: poster.id, storagePath: poster.storage_path });
                  }}
                  className='absolute bottom-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600'
                  title='Delete poster'
                >
                  <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                  </svg>
                </button>
              )}
            </div>
          </div>
        ))}

        {/* ── Collaboration cards ── */}
        {collaborations.map(collaboration => (
          <div
            key={collaboration.id}
            className='bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md hover:scale-105 transition-all duration-300'
          >
            {/* Project Image */}
            <div className='relative'>
              <div
                onClick={() =>
                  onCollaborationClick && onCollaborationClick(collaboration)
                }
                className='block cursor-pointer'
              >
                <CollaborationImage
                  imageKey={collaboration.posterPreview}
                  alt={collaboration.title}
                  className='w-full aspect-square object-cover'
                />
              </div>
              {/* Bookmark Button */}
              <button
                onClick={e => handleBookmarkClick(e, collaboration.id)}
                className='absolute top-3 right-3 p-2 bg-white bg-opacity-90 rounded-full hover:bg-opacity-100 transition-all duration-200'
                title={bookmarkStates[collaboration.id] ? '取消收藏' : '收藏'}
              >
                <svg
                  className={`w-5 h-5 ${
                    bookmarkStates[collaboration.id]
                      ? 'text-purple-500 fill-current'
                      : 'text-gray-400'
                  }`}
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z'
                  />
                </svg>
              </button>
            </div>

            {/* Collaboration Content */}
            <div className='p-3'>
              <div
                onClick={() =>
                  onCollaborationClick && onCollaborationClick(collaboration)
                }
                className='block cursor-pointer'
              >
                <h3 className='font-bold text-gray-900 mb-2 text-sm hover:text-purple-600 transition-colors duration-200'>
                  {collaboration.title}
                </h3>
                {/* Description removed from TAG Me list view to save space */}
              </div>

              {/* Author */}
              <div className='flex items-center'>
                {collaboration.author.avatar ? (
                  <img
                    src={collaboration.author.avatar}
                    alt={collaboration.author.name}
                    className='w-6 h-6 rounded-full mr-2 object-cover'
                    onError={e => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className='w-6 h-6 rounded-full mr-2 bg-gray-300 flex items-center justify-center'>
                    <span className='text-xs text-gray-600 font-medium'>
                      {collaboration.author.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className='text-xs font-medium text-gray-900'>
                  {collaboration.author.name}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 2026-04-17 登录守卫弹窗（未登录点击收藏时触发） */}
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={handleLoginModalClose}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  );
};

export default CollaborationGrid;
